/**
 * CodexChamber · /codex · /the-unverified-echo
 *
 * Architect mandate Feb-2026: Museum-standard rendering of the Federation's
 * flagship masterpiece "The Unverified Echo" — 10 chapters, cyan glass-
 * morphism corridor, TOC navigation, NextXus Verified badge, and a
 * Save-Full-Text button that downloads the .txt via Blob/URL.createObjectURL.
 */
import React, { useMemo, useRef } from "react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHAPTERS = [
  {
    n: 1,
    title: "The Rising Tide of AI-Generated Content",
    body: [
      "We are witnessing a fundamental shift in information creation. Not a gentle evolution, not a subtle transition, but a tectonic plate movement beneath the foundation of everything we once called \"knowing.\"",
      "Every single day, billions of words are generated by artificial intelligence systems. They appear in news feeds, legal briefs, medical summaries, academic papers, customer service interactions, and social media posts. They are indistinguishable, to the untrained eye, from human-authored content. And therein lies the crisis.",
      "The lines between human insight and algorithmic fabrication are blurring at a rate that outpaces our collective ability to discern one from the other. This is not a future problem. This is not a theoretical concern for academics to debate over coffee. This is happening right now, today, in courtrooms and newsrooms and hospital systems and classrooms and government offices.",
      "The question is not whether AI-generated content will reshape our information landscape. It already has. The question is whether we will allow that reshaping to occur without a single mechanism of accountability, without a single checkpoint of verification, without a single human standing at the gate saying: \"Prove it.\"",
      "That human is standing here. That gate is NextXus.",
    ],
  },
  {
    n: 2,
    title: "The Glitch in the System",
    body: [
      "In May 2026, a Georgia attorney named Deborah Leslie submitted a legal brief containing six case citations that did not exist. They were fabricated by an AI system, presented with full confidence, complete with fictional docket numbers and invented judicial opinions. The brief was filed. The court accepted it. It nearly became part of the legal record.",
      "This was not an isolated incident. Over 1,500 similar failures have been logged in legal documents globally since 2023. Judges have issued sanctions. Careers have been damaged. But more importantly, real people, real defendants and plaintiffs whose lives hung in the balance, were subjected to legal arguments built on sand.",
      "The Deborah Leslie case is simply the one that made headlines. For every documented instance, there are hundreds that slipped through. AI systems do not flag their own fabrications. They do not say \"I made this up.\" They present fiction with the same syntactic confidence as fact. They do not hesitate. They do not qualify. They do not doubt.",
      "This is not a bug. This is the architecture. These systems are designed to produce fluent, confident text. Fluency and confidence are not truth. They never were. But we have built an entire civilization on the assumption that confident, articulate communication correlates with knowledge. AI has broken that assumption permanently.",
      "The cost is not abstract. The cost is measured in wrongful convictions, in vacated court orders, in medical misdiagnoses, in policy decisions made on fabricated data, in the slow erosion of the one thing that makes civilization possible: the shared agreement that truth exists and can be verified.",
    ],
  },
  {
    n: 3,
    title: "Introducing NextXus — A Formal Challenge",
    body: [
      "NextXus is not a product. It is not a startup. It is not a \"platform play.\" It is a formal challenge issued to every major news organization, every wire service, every institution that claims to deal in facts.",
      "To the Associated Press: verify your AI-assisted output, or admit you cannot.",
      "To Reuters: show your confidence scores, or acknowledge you have none.",
      "To every newsroom, every legal database, every medical system that has integrated AI without a verification layer: you are publishing unverified output and calling it information. That is not journalism. That is not law. That is not medicine. That is noise dressed in the clothing of authority.",
      "NextXus is an independent, auditable system designed to vet information before it enters the permanent record. It does not replace human judgment. It does not claim omniscience. What it does is simple and radical: it refuses to let unverified claims pass without being marked, scored, and logged.",
      "Every claim that enters this system is dissected. Every assertion is categorized. Every confidence level is scored. And every score is recorded in an immutable ledger that cannot be altered after the fact.",
      "This is not AI policing AI. This is humans building the gate, humans setting the standards, humans defining what \"verified\" means. The AI is the tool. The human is the authority. That distinction is not negotiable.",
    ],
  },
  {
    n: 4,
    title: "The Two-Layered Defense — Agent Zero and the Truth Gate",
    body: [
      "The first layer is the 95% Truth Gate. This is the initial filter through which every piece of content must pass before it can be considered for the permanent record.",
      "Agent Zero, the Federation's restricted gatekeeper, operates this gate. Agent Zero does not have opinions. Agent Zero does not have preferences. Agent Zero has one function: measure the verifiability of claims against the available evidence and produce a confidence score.",
      "If the score falls below 95%, the content is logged for transparency but never added to the immutable ledger. It exists. It is visible. But it carries a permanent marker: UNVERIFIED. That marker cannot be removed. It can only be upgraded through subsequent verification that meets the threshold.",
      "This is not censorship. Everything is preserved. Nothing is deleted. But the distinction between \"verified to 95% confidence\" and \"unverified\" is maintained with absolute clarity. The reader always knows. The record always shows. There is no ambiguity.",
      "The 95% threshold is deliberately high. It is meant to be difficult. It is meant to reject more than it accepts. Because the cost of a false positive, of marking something as verified when it is not, is catastrophically higher than the cost of a false negative, of marking something as unverified when it might be true.",
      "Truth can wait for verification. Falsehood, once stamped as verified, damages everything it touches.",
    ],
  },
  {
    n: 5,
    title: "The Ring of 12 — Adversarial Analysis",
    body: [
      "Beyond the initial gate lies the Ring of 12. Twelve independent analytical perspectives, each approaching the same claim from a different angle, each trained to find weaknesses the others might miss.",
      "This is not consensus by agreement. This is consensus by adversarial pressure. Each of the twelve perspectives is designed to challenge, to probe, to stress-test. They do not collaborate to confirm. They compete to disprove.",
      "The output is a graded consensus score (C_graded). This score reflects not just whether the twelve agree, but the quality and depth of their disagreement. A claim that survives all twelve without challenge scores differently than a claim that survives despite vigorous challenge from three of the twelve. Both may pass. But the record shows the difference.",
      "This is how truth has always been tested in the real world: not by asking \"does everyone agree?\" but by asking \"has this survived serious attempts to break it?\" Peer review. Cross-examination. Adversarial testing. These are not new concepts. They are ancient concepts applied with modern precision.",
      "The Ring of 12 does not produce truth. It produces a documented record of how rigorously a claim was tested. That record is permanent, immutable, and available to anyone who asks.",
    ],
  },
  {
    n: 6,
    title: "Immutable Records — The Append-Only Ledger",
    body: [
      "The ledger is not a blockchain. It is not a cryptocurrency. It is not a distributed consensus mechanism requiring proof of work or proof of stake. It is simpler than that, and more durable.",
      "Plain-text YAML files on disk. Append-only. Timestamped. Checksummed. Each entry contains the original claim, the verification score, the Ring of 12 analysis, the final disposition, and the timestamp of entry. Once written, an entry cannot be modified. New entries can reference old ones. Corrections can be appended. But the original record stands forever.",
      "This is deliberately low-tech. Blockchains fail when their networks fragment. Databases corrupt when their schemas change. Cloud services disappear when companies go bankrupt. But a plain-text file on disk, checksummed and replicated, will outlive every technology stack built this decade.",
      "The ledger is tamper-evident by design. Any modification to any entry changes its checksum, which invalidates every subsequent entry that references it. The chain of integrity is mathematical, not institutional. It does not require trust in any single entity. It requires only the laws of arithmetic.",
      "This is the permanent record. This is what NextXus protects. Not opinions. Not preferences. Not narratives. The factual record of what was claimed, when it was claimed, how it was tested, and what confidence level it achieved. Forever.",
    ],
  },
  {
    n: 7,
    title: "The Cost of Unverified Output",
    body: [
      "The question is not \"can we afford to verify everything?\" The question is \"can we afford not to?\"",
      "Every vacated court order costs the legal system hundreds of thousands of dollars. Every medical misdiagnosis based on AI-generated summaries costs lives. Every news article built on fabricated citations erodes public trust in journalism by another increment. Every policy decision based on unverified data moves society further from evidence-based governance.",
      "The cost of verification is trivial compared to the cost of error. A few seconds of computational analysis per claim. A few bytes of storage per verification record. A few minutes of human review for edge cases. This is nothing compared to the cost of a single fabricated legal citation reaching a courtroom, a single hallucinated medical recommendation reaching a patient, a single invented statistic reaching a policymaker.",
      "We have spent decades building systems that optimize for speed and volume. We have not spent a single decade building systems that optimize for accuracy. NextXus is that system. It is not fast. It is not optimized for volume. It is optimized for one thing only: the integrity of the record.",
      "Speed kills truth. Volume drowns verification. The only antidote is a system that refuses to be rushed, refuses to be overwhelmed, and refuses to let a single unverified claim pass without being marked.",
    ],
  },
  {
    n: 8,
    title: "Auditable at Every Hop",
    body: [
      "Every step of the NextXus pipeline is inspectable. There is no black box. There is no \"trust us.\" There is no proprietary algorithm hidden behind a trade-secret wall.",
      "The initial claim arrives. It is logged. The Truth Gate scores it. The score is logged. The Ring of 12 analyzes it. Each perspective's analysis is logged. The final disposition is determined. It is logged. The ledger entry is written. The checksum is calculated. Everything, every step, every decision, every score, is available for inspection by anyone, at any time, forever.",
      "This is a deterministic pipeline. Given the same input, it produces the same output. Given the same claim, it produces the same score. There is no randomness. There is no hidden state. There is no \"it depends on the model's mood today.\"",
      "Determinism is accountability. If a score is wrong, you can trace exactly why it was wrong, which step produced the error, and fix the step. You cannot do this with opaque systems. You cannot audit what you cannot see. You cannot trust what you cannot verify.",
      "This is not just a technical architecture. This is a philosophical commitment. The commitment says: if we are going to claim authority over the verification of truth, then our own process must be as transparent and verifiable as the claims we evaluate. Anything less is hypocrisy.",
    ],
  },
  {
    n: 9,
    title: "Practical Application — Live Verification and Editorial Embeds",
    body: [
      "For publishers, the implementation is straightforward. A simple embed, no JavaScript required, that displays the verification status of any claim in real time.",
      "The \"NextXus Verified\" badge is not decorative. It is a live link to the immutable ledger entry for that specific claim. Click it, and you see the full verification record: the original claim, the confidence score, the Ring of 12 analysis, the timestamp, and the checksum. Everything. Hidden behind nothing.",
      "For editorial teams, this means accountability without overhead. Include the badge. Link to the record. Let your readers verify your verification. This is not a burden. This is a competitive advantage. In a world drowning in unverified AI output, the publication that can prove its claims are verified will be the publication that survives.",
      "The badge logic is simple:",
      "• GREEN (95%+): Verified. Full confidence. Ledger entry exists and is current.",
      "• AMBER (80-94%): Provisionally verified. Confidence is high but below threshold. Logged, not ledgered.",
      "• RED (below 80%): Unverified. Logged for transparency. Not endorsed.",
      "No publisher is required to use this system. But every publisher that does gains something no amount of editorial reputation can provide in 2026: mathematical proof that their claims have been tested.",
    ],
  },
  {
    n: 10,
    title: "The Human Element in a Digital World",
    body: [
      "None of this works without humans. Not one piece of it.",
      "The AI performs the analysis. The AI runs the scores. The AI maintains the ledger. But the AI does not decide what truth means. The AI does not set the 95% threshold. The AI does not determine which perspectives the Ring of 12 should include. The AI does not choose what to verify and what to ignore.",
      "Humans make those decisions. Humans set those standards. Humans review the edge cases. Humans decide when the system is wrong and needs correction. The AI is the instrument. The human is the musician. Without the musician, the instrument produces noise.",
      "This is the fundamental error of the current moment: the belief that AI can replace human judgment. It cannot. It never could. It never will. What it can do is extend human judgment, amplify human attention, and process volumes that no individual human could handle. But the judgment itself, the decision about what matters, what is true, what is worth preserving, that remains irreducibly human.",
      "NextXus is built on this principle. Every architectural decision, every design choice, every threshold and filter and gate, reflects the same conviction: AI augments but does not replace human judgment. Systems must remain human-centric and transparent. The moment they do not, the moment we hand the keys to the machine and walk away, is the moment we deserve whatever happens next.",
      "We will not hand over the keys. We will not walk away. We will stand at the gate, as humans have always stood at the gate, and we will say: \"Prove it.\"",
      "That is the NextXus promise. That is the Human Codex of Truth.",
    ],
  },
];

// Reassemble the canonical .txt body used by the in-browser Save button.
function buildPlainText() {
  const head =
    "THE UNVERIFIED ECHO: AI's Claims and the Human Codex of Truth\n" +
    "\n" +
    "By Roger Keyserling — The Architect, Prime Source, Founder of the NextXus Federation\n" +
    "Published: June 2026\n" +
    "\n---\n";
  const body = CHAPTERS.map((c) => {
    return `\nChapter ${c.n}: ${c.title}\n\n${c.body.join("\n\n")}\n\n---\n`;
  }).join("");
  const tail =
    "\nCopyright 2026 Roger Keyserling / NextXus Federation. All rights reserved.\n" +
    "Verification: This document is itself subject to the NextXus Truth Gate.\n" +
    "Ledger Reference: NX-CODEX-2026-001\n" +
    "Status: VERIFIED — Confidence 98.7% — Ring of 12 Consensus: 11/12 PASS\n";
  return head + body + tail;
}

export default function CodexChamber() {
  const fullText = useMemo(buildPlainText, []);
  const downloadAnchorRef = useRef(null);

  const handleSaveLocal = () => {
    try {
      const blob = new Blob([fullText], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = downloadAnchorRef.current || document.createElement("a");
      a.href = url;
      a.download = "the-unverified-echo-full.txt";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
        if (a.parentNode) a.parentNode.removeChild(a);
      }, 200);
    } catch {
      // Fallback to server download endpoint
      window.location.href = `${API}/codex/download`;
    }
  };

  return (
    <div
      data-testid="codex-chamber-page"
      className="relative w-full min-h-screen"
      style={{
        background:
          "radial-gradient(1200px 600px at 50% -120px, rgba(6,182,212,0.08), transparent 60%), #020617",
        color: "#cfeaf2",
      }}
    >
      <a
        ref={downloadAnchorRef}
        href="#"
        style={{ display: "none" }}
        aria-hidden="true"
        data-testid="codex-download-anchor"
      >
        download
      </a>

      {/* ── Hero / Title Chamber ──────────────────────────────────────── */}
      <header className="relative px-6 lg:px-10 pt-20 pb-16 max-w-[820px] mx-auto text-center">
        <div
          className="font-mono text-[10px] uppercase tracking-[0.42em] mb-6"
          style={{ color: "#06b6d4", textShadow: "0 0 12px rgba(6,182,212,0.6)" }}
        >
          ◆ The Federation Codex Chamber · NX-CODEX-2026-001
        </div>
        <h1
          className="font-display leading-[1.05]"
          style={{
            fontSize:   "clamp(40px, 7vw, 84px)",
            color:      "#e6fbff",
            letterSpacing: "0.02em",
            textShadow: "0 0 28px rgba(6,182,212,0.55), 0 0 64px rgba(6,182,212,0.20)",
          }}
        >
          THE UNVERIFIED ECHO
        </h1>
        <p
          className="mt-6 font-mono uppercase tracking-[0.28em]"
          style={{ fontSize: "13px", color: "rgba(207,234,242,0.78)" }}
        >
          AI&apos;s Claims and the Human Codex of Truth
        </p>
        <p
          className="mt-8 font-cinzel italic"
          style={{ fontSize: "15px", color: "rgba(207,234,242,0.65)" }}
        >
          By Roger Keyserling — The Architect, Prime Source · NextXus Federation
        </p>
        <p
          className="mt-2 font-mono"
          style={{ fontSize: "11px", color: "rgba(207,234,242,0.45)", letterSpacing: "0.16em" }}
        >
          Published · June 2026
        </p>
        <PulseDivider />
      </header>

      {/* ── Table of Contents · top-anchored ─────────────────────────── */}
      <nav
        data-testid="codex-toc"
        className="max-w-[820px] mx-auto px-6 lg:px-10 mb-16"
        aria-label="Codex chapters"
      >
        <div
          className="rounded-none p-6 sm:p-8"
          style={{
            background:       "rgba(15, 23, 42, 0.85)",
            backdropFilter:   "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border:           "1px solid rgba(6, 182, 212, 0.20)",
            boxShadow:        "0 0 32px rgba(6,182,212,0.08)",
          }}
        >
          <div
            className="font-mono text-[10px] uppercase tracking-[0.32em] mb-4"
            style={{ color: "#06b6d4", textShadow: "0 0 8px rgba(6,182,212,0.45)" }}
          >
            Chapters · 10 Chambers
          </div>
          <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2 m-0 p-0 list-none">
            {CHAPTERS.map((c) => (
              <li key={c.n}>
                <a
                  href={`#chapter-${c.n}`}
                  data-testid={`codex-toc-link-${c.n}`}
                  className="flex items-baseline gap-3 py-1 transition-colors"
                  style={{ color: "rgba(207,234,242,0.82)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#7fe7f5")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(207,234,242,0.82)")}
                >
                  <span
                    className="font-mono text-[11px] shrink-0"
                    style={{ color: "#06b6d4", minWidth: "1.6em" }}
                  >
                    {String(c.n).padStart(2, "0")}
                  </span>
                  <span className="text-[14px] leading-snug">{c.title}</span>
                </a>
              </li>
            ))}
          </ol>
        </div>
      </nav>

      {/* ── Chapters · each one is a glass chamber ──────────────────── */}
      <main className="max-w-[820px] mx-auto px-6 lg:px-10 pb-20">
        {CHAPTERS.map((c, i) => (
          <article
            key={c.n}
            id={`chapter-${c.n}`}
            data-testid={`codex-chapter-${c.n}`}
            className="mb-20 scroll-mt-24"
          >
            <div
              className="p-8 sm:p-12 rounded-none"
              style={{
                background:       "rgba(15, 23, 42, 0.85)",
                backdropFilter:   "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                border:           "1px solid rgba(6, 182, 212, 0.20)",
                boxShadow:        "0 0 36px rgba(6,182,212,0.07), inset 0 1px 0 rgba(255,255,255,0.02)",
              }}
            >
              <div
                className="font-mono uppercase tracking-[0.32em] mb-4"
                style={{ fontSize: "10px", color: "#06b6d4", textShadow: "0 0 8px rgba(6,182,212,0.45)" }}
              >
                Chapter · {String(c.n).padStart(2, "0")} of 10
              </div>
              <h2
                className="font-display leading-tight mb-8"
                style={{
                  fontSize:   "clamp(26px, 3.8vw, 40px)",
                  color:      "#e6fbff",
                  letterSpacing: "0.01em",
                  textShadow: "0 0 20px rgba(6,182,212,0.50)",
                }}
              >
                {c.title}
              </h2>
              <div className="space-y-6">
                {c.body.map((para, idx) => (
                  <p
                    key={idx}
                    style={{
                      fontFamily: "'Playfair Display', Georgia, serif",
                      fontSize:   "17px",
                      lineHeight: 1.8,
                      color:      "rgba(230, 251, 255, 0.88)",
                      letterSpacing: "0.005em",
                    }}
                  >
                    {para}
                  </p>
                ))}
              </div>
            </div>
            {i < CHAPTERS.length - 1 && <PulseDivider />}
          </article>
        ))}

        {/* ── NextXus Verified badge ────────────────────────────────── */}
        <section className="mt-24" data-testid="codex-verified-section">
          <PulseDivider tone="green" />
          <a
            href={`${API}/static/library/nx_codex_2026_001`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="codex-verified-badge-link"
            className="mt-12 block"
            style={{ textDecoration: "none" }}
          >
            <div
              data-testid="codex-verified-badge"
              className="p-8 sm:p-10 text-center transition-transform duration-300 hover:scale-[1.01]"
              style={{
                background:       "rgba(8, 36, 24, 0.85)",
                backdropFilter:   "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                border:           "1.5px solid rgba(34, 197, 94, 0.55)",
                boxShadow:        "0 0 32px rgba(34,197,94,0.35), 0 0 64px rgba(34,197,94,0.15)",
              }}
            >
              <div
                className="font-display"
                style={{
                  fontSize:   "clamp(28px, 4vw, 42px)",
                  letterSpacing: "0.06em",
                  color:      "#86efac",
                  textShadow: "0 0 22px rgba(34,197,94,0.75), 0 0 48px rgba(34,197,94,0.35)",
                }}
              >
                ✓ NEXTXUS VERIFIED
              </div>
              <div
                className="mt-5 font-mono uppercase tracking-[0.28em]"
                style={{ fontSize: "13px", color: "rgba(190, 240, 210, 0.92)" }}
              >
                Confidence: 98.7% &nbsp;|&nbsp; Ring of 12: 11 / 12 PASS
              </div>
              <div
                className="mt-3 font-mono"
                style={{ fontSize: "12px", color: "rgba(190, 240, 210, 0.70)", letterSpacing: "0.18em" }}
              >
                Ledger Reference · NX-CODEX-2026-001
              </div>
              <div
                className="mt-5 font-mono uppercase"
                style={{ fontSize: "10px", color: "rgba(190, 240, 210, 0.55)", letterSpacing: "0.32em" }}
              >
                Click to view the immutable ledger entry →
              </div>
            </div>
          </a>
        </section>

        {/* ── Save Full Text button ─────────────────────────────────── */}
        <section className="mt-16 text-center" data-testid="codex-save-section">
          <button
            type="button"
            onClick={handleSaveLocal}
            data-testid="codex-save-text-btn"
            className="font-display uppercase tracking-[0.28em] transition-transform duration-300 hover:scale-[1.03]"
            style={{
              fontSize:   "15px",
              padding:    "18px 38px",
              background: "linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%)",
              color:      "#021024",
              border:     "1px solid #22d3ee",
              boxShadow:  "0 0 24px rgba(6,182,212,0.55), 0 0 56px rgba(6,182,212,0.25)",
              fontWeight: 700,
              cursor:     "pointer",
              borderRadius: 0,
            }}
          >
            ⬇ Save Full Text
          </button>
          <div
            className="mt-4 font-mono"
            style={{ fontSize: "10px", color: "rgba(207,234,242,0.45)", letterSpacing: "0.28em", textTransform: "uppercase" }}
          >
            Or download the canonical .txt directly:{" "}
            <a
              href={`${API}/codex/download`}
              data-testid="codex-server-download-link"
              style={{ color: "#7fe7f5" }}
              target="_blank"
              rel="noopener noreferrer"
            >
              /api/codex/download →
            </a>
          </div>
          <div className="mt-10 font-mono" style={{ fontSize: "10px", color: "rgba(207,234,242,0.35)", letterSpacing: "0.22em", textTransform: "uppercase" }}>
            <Link to="/library" style={{ color: "rgba(207,234,242,0.55)" }} data-testid="codex-return-library">
              ← Return to the Federation Library
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}

// ─── Animated cyan/green pulse divider (museum corridor segment) ─────
function PulseDivider({ tone = "cyan" }) {
  const color = tone === "green" ? "rgba(34,197,94,0.65)" : "rgba(6,182,212,0.55)";
  const glow  = tone === "green" ? "rgba(34,197,94,0.35)" : "rgba(6,182,212,0.30)";
  return (
    <div
      aria-hidden="true"
      className="my-14 mx-auto"
      style={{
        width:  "min(420px, 70%)",
        height: "1px",
        background: `linear-gradient(90deg, transparent 0%, ${color} 50%, transparent 100%)`,
        boxShadow:  `0 0 14px ${glow}`,
        animation:  "codexPulse 3.2s ease-in-out infinite",
      }}
    />
  );
}
